import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Flex } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import asBentoPage from '../../../../../../common/bento/hoc/asBentoPage';
import Button from '../../../../../../components/_core/Button';
import Callout from '../../../../../../components/_core/Callout';
import * as FormComponents from '../../../../../../components/_core/form';
import Heading from '../../../../../../components/_core/Heading';
import SunshineCard from '../../../../../../components/_core/SunshineCard';
import SunshineIcon from '../../../../../../components/_core/SunshineIcon';
import Text from '../../../../../../components/_core/Text';
import OBNavigation from '../../../../components/OBNavigation/OBNavigation';
import { GetBeneficiariesDataQuery } from '../../graphql/queries/getBeneficiariesData.gql';
import confirmBeneficiariesFormSchema, {
  ConfirmBeneficiariesFormSchema,
} from './confirmBeneficiariesForm.schema';
import locales from './locales';

interface ConfirmBeneficiariesProps {
  data: GetBeneficiariesDataQuery['viewer'];
  onSubmit: (formValues: ConfirmBeneficiariesFormSchema) => void;
  isSaving: boolean;
}

const ConfirmBeneficiaries = asBentoPage<ConfirmBeneficiariesProps>(
  ({ data, isSaving, Layout, onPrev, onSubmit }) => {
    const [emailsMustBeUniqueError, setEmailsMustBeUniqueError] =
      useState<boolean>(false);

    const filteredBeneficiaries = data.company.legalBeneficiaries.filter(
      (legalBeneficiary) => legalBeneficiary.invitation,
    );

    const applicants = filteredBeneficiaries.map((legalBeneficiary) => ({
      email: legalBeneficiary.invitation?.inviteeEmail ?? '',
      id: legalBeneficiary.companyLegalBeneficiaryId,
    }));

    const {
      formState: { errors },
      handleSubmit: rhfHandleSubmit,
      register,
    } = useForm({
      defaultValues: { applicants },
      mode: 'onBlur',
      resolver: zodResolver(confirmBeneficiariesFormSchema),
    });

    const handleSubmit = rhfHandleSubmit((formValues) => {
      const emailSet = new Set([data.companyUser.email]);
      for (const applicant of formValues.applicants) {
        if (emailSet.has(applicant.email)) {
          setEmailsMustBeUniqueError(true); // Set the error state to true if a duplicate is found
          return;
        }
        emailSet.add(applicant.email);
      }

      setEmailsMustBeUniqueError(false);

      onSubmit(formValues);
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Heading marginBottom="space-16" size="lg">
          {locales.title}
        </Heading>
        <Text marginBottom="space-48" variant="primary">
          {locales.description}
        </Text>
        <Flex alignItems="center" marginBottom="space-16">
          <SunshineIcon marginRight="space-8" name="mail" />
          <Text> {locales.listHeading}</Text>
        </Flex>

        <form noValidate onSubmit={handleSubmit}>
          {filteredBeneficiaries.map(
            ({ companyLegalBeneficiaryId, firstName, lastName }, index) => (
              <SunshineCard
                key={companyLegalBeneficiaryId}
                marginBottom="space-16"
              >
                <SunshineCard.Text
                  fontWeight="weight-500"
                  marginBottom="space-10"
                  size="md"
                  variant="primary"
                >
                  {`${firstName} ${lastName}`}
                </SunshineCard.Text>
                <SunshineCard.Content>
                  <FormComponents.Field
                    error={errors.applicants?.[index]?.email?.message}
                    label={locales.emailInputLabel}
                    width="100%"
                  >
                    <FormComponents.Input
                      disabled={false}
                      type="email"
                      {...register(
                        `applicants.${applicants.findIndex(
                          (applicant) =>
                            applicant.id === companyLegalBeneficiaryId,
                        )}.email`,
                      )}
                    />
                  </FormComponents.Field>
                </SunshineCard.Content>
              </SunshineCard>
            ),
          )}
          {emailsMustBeUniqueError ? (
            <Callout hideIcon marginTop="space-16" variant="error">
              {locales.emailsMustBeUniqueError}
            </Callout>
          ) : null}
          <Button isLoading={isSaving} marginTop="space-32" type="submit">
            {locales.cta}
          </Button>
        </form>
      </Layout>
    );
  },
);
export default ConfirmBeneficiaries;
